import type { ReactNode } from 'react';
import { useEffect } from 'react';
import { useLocation } from 'react-router-dom';

interface ApiPassthroughProps {
  /** Rendered when the SPA document itself was served at an /api URL (see below). */
  fallback: ReactNode;
}

/**
 * Hands `/api/*` back to the network. On the portal domains, Front Door routes `/api/*` to the
 * frontline APIM, so the SPA router must never render anything there. Mount this on `/api/*`
 * ahead of the auth guard and the `*` catch-all: an in-app navigation to an /api path then becomes
 * a real page load that Front Door can route, instead of the SPA's 404 page.
 *
 * IMPORTANT: when the document itself was loaded at the /api URL, the static site answered a
 * request Front Door should have routed to APIM (e.g. while a route change is still propagating).
 * Reloading would just fetch the same index.html again, forever, so render `fallback` instead.
 */
export function ApiPassthrough({ fallback }: ApiPassthroughProps): ReactNode {
  const { pathname, search, hash } = useLocation();
  const url = `${window.location.origin}${pathname}${search}`;
  const servedAsDocument = isDocumentUrl(url);

  useEffect(() => {
    if (!servedAsDocument) {
      // replace: the router already pushed this URL onto history, so don't add a second entry.
      window.location.replace(`${url}${hash}`);
    }
  }, [url, hash, servedAsDocument]);

  return servedAsDocument ? fallback : null;
}

/** True when the current document was loaded (not client-side routed) at `url`, ignoring the fragment. */
function isDocumentUrl(url: string): boolean {
  const [entry] = performance.getEntriesByType('navigation');
  return entry !== undefined && entry.name.split('#')[0] === url;
}
